import { useEffect, useState } from 'react';

import type { GameApi } from '../state/useGame.ts';
import { DAILY_SCORE_ATTACK, LADDER } from '../engine/modes.ts';
import { copyFor } from './modeCopy.ts';
import { isCleared, tilesRemaining } from '../engine/game.ts';
import {
  beatPercent,
  fetchLeaderboard,
  submitResult,
  type Leaderboard,
} from '../state/api.ts';
import { markBestConfirmed, playerId, playerName } from '../state/persist.ts';
import { LADDER_RUNGS } from '../engine/board.ts';
import { ACHIEVEMENTS_PER_DAY } from '../engine/achievements.ts';
import { HUNT } from '../engine/modes.ts';
import { shareResult } from '../state/share.ts';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | {
      kind: 'done';
      score: number;
      rank: number;
      improved: boolean;
      /** Percent of today's other players beaten, or null when too few have played. */
      beat: number | null;
      players: number;
    }
  | { kind: 'failed'; reason: string };

export function FinishedScreen({ game }: { game: GameApi }): React.ReactElement {
  const state = game.state!;
  const puzzle = game.puzzle!;

  const cleared = isCleared(state.board);
  const remaining = tilesRemaining(state.board);
  const isLadder = game.mode.id === LADDER.id;
  const copy = copyFor(game.mode.id);

  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });
  const [board, setBoard] = useState<Leaderboard | null>(null);
  const [boardError, setBoardError] = useState<string | null>(null);
  const [shareNote, setShareNote] = useState<string | null>(null);
  const [name, setName] = useState(game.name ?? playerName() ?? '');

  let longest = 0;
  let bestWordScore = 0;

  for (const move of state.moves) {
    if (move.word.length > longest) longest = move.word.length;
    if (move.score > bestWordScore) bestWordScore = move.score;
  }

  // The ACTIVE mode's metrics, not a hardcoded one. Ladder reports which rungs it
  // climbed; Score Attack reports the best and longest word.
  const metrics = game.mode.metrics({
    board: state.board,
    moves: state.moves,
    score: state.score,
    cleared,
    tilesRemaining: remaining,
  });

  // Submit once, automatically, when the game ends. The score is not sent - only the
  // moves - and what comes back is the server's own arithmetic.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setSubmitState({ kind: 'sending' });

      try {
        const outcome = await submitResult({
          puzzleId: puzzle.puzzleId,
          playerId: playerId(),
          name: playerName(),
          // The whole log, not just the words: a shuffle changed the board, so leaving
          // it out would make the Worker replay every later word somewhere else.
          moves: state.actions.map((action) =>
            action.kind === 'shuffle' ? ({ shuffle: true } as const) : action.tiles,
          ),
          rulesVersion: puzzle.rulesVersion,
          dictionaryVersion: puzzle.dictionaryVersion,
        });

        if (cancelled) return;

        if (outcome.verified) markBestConfirmed(puzzle.puzzleId);

        setSubmitState({
          kind: 'done',
          score: outcome.score,
          rank: outcome.rank,
          improved: outcome.improved,
          beat: beatPercent(outcome),
          players: outcome.players ?? 1,
        });
      } catch (thrown: unknown) {
        if (cancelled) return;

        setSubmitState({
          kind: 'failed',
          reason: thrown instanceof Error ? thrown.message : 'it could not be sent',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [puzzle, state.actions]);

  async function openLeaderboard(fresh = false): Promise<void> {
    setBoardError(null);

    try {
      setBoard(await fetchLeaderboard(puzzle.puzzleId, playerId(), fresh));
    } catch (thrown: unknown) {
      setBoardError(
        thrown instanceof Error ? thrown.message : 'the leaderboard could not be loaded',
      );
    }
  }

  async function onShare(): Promise<void> {
    const claimed = state.moves.reduce((total, move) => total + move.claimed.length, 0);
    const rungsDone = new Set(
      state.moves
        .map((move) => move.word.length)
        .filter((length) => LADDER_RUNGS.includes(length)),
    ).size;

    const outcome = await shareResult({
      title: game.mode.id === DAILY_SCORE_ATTACK.id ? 'Word Soup' : game.mode.title,
      displayNumber: puzzle.displayNumber,
      score: state.score,
      // Lengths and tiers only. The words themselves are never in a share.
      words: state.moves.map((move) => ({ length: move.word.length, tier: move.tier })),
      cleared,
      tilesRemaining: remaining,
      puzzleId: puzzle.puzzleId,
      streak: game.streak,
      beatPercent: submitState.kind === 'done' ? submitState.beat : null,
      ...(isLadder ? { rungs: { done: rungsDone, total: LADDER_RUNGS.length } } : {}),
      ...(game.mode.id === HUNT.id
        ? {
            achievements: {
              claimed,
              total: puzzle.achievements?.length ?? ACHIEVEMENTS_PER_DAY,
            },
          }
        : {}),
    });

    if (outcome.kind === 'copied') setShareNote('Copied to your clipboard.');
    else if (outcome.kind === 'shared') setShareNote(null);
    else if (outcome.reason) setShareNote(`Could not share: ${outcome.reason}.`);
  }

  // The board opens ITSELF once the score is saved. It was behind a button, which meant
  // most players never saw that anybody else had played - and a daily puzzle whose
  // leaderboard nobody opens is a single-player game with extra steps.
  useEffect(() => {
    if (submitState.kind !== 'done' || board !== null) return;

    void openLeaderboard();
    // openLeaderboard is stable for this puzzle; re-running on every render would poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitState.kind]);

  const confirmed = submitState.kind === 'done';

  return (
    <div className="finished">
      <p className="wordmark wordmark--small">
        word soup<span className="dot">.</span>
      </p>

      <h1 className="finished__headline">
        {/* Only ever "Board cleared" when the board is genuinely empty. For Ladder the
            two coincide - the rungs use up the grid exactly - so a finished ladder can
            say so plainly. */}
        {cleared
          ? isLadder
            ? 'Ladder complete.'
            : 'Board cleared.'
          : isLadder
            ? 'Ladder unfinished.'
            : 'Puzzle complete.'}
      </h1>

      <p className="finished__sub">
        {copy.label} #{puzzle.displayNumber} ·{' '}
        {cleared
          ? isLadder
            ? 'all five rungs'
            : 'every tile used'
          : `${remaining} tile${remaining === 1 ? '' : 's'} left on the board`}
      </p>

      <div>
        <div className="finished__score">{state.score}</div>
        <div className="finished__score-label">
          {confirmed ? 'Points' : 'Points · on this device'}
        </div>
      </div>

      <div className="metrics">
        {metrics.map((metric) => (
          <div className="metrics__row" key={metric.id}>
            <span className="metrics__label">{metric.label}</span>
            <span className="metrics__value">{metric.value}</span>
          </div>
        ))}
      </div>

      <p
        className={
          submitState.kind === 'done' && submitState.improved
            ? 'finished__status finished__status--good'
            : 'finished__status'
        }
        role="status"
      >
        {submitState.kind === 'sending' ? 'Saving your score…' : null}
        {submitState.kind === 'done'
          ? submitState.improved
            ? `Saved — a new best for today, ${submitState.rank === 1 ? 'top of the board' : `#${submitState.rank}`}.`
            : 'Saved. Your earlier score for today was better, so that one stands.'
          : null}
        {/* Where that stands among everybody else who played today. Below a handful of
            players a percentage is noise, so it says the plain count instead. */}
        {submitState.kind === 'done' ? (
          <span className="finished__standing">
            {submitState.beat !== null
              ? ` Beat ${submitState.beat}% of today's players.`
              : submitState.players > 1
                ? ` ${submitState.players} have played today.`
                : ' First one in today.'}
          </span>
        ) : null}
        {submitState.kind === 'failed'
          ? `Kept on this device only — ${submitState.reason}.`
          : null}
      </p>

      {game.streak > 1 ? (
        <p className="finished__streak">🔥 {game.streak} days in a row</p>
      ) : null}

      <div className="entry__actions" style={{ marginTop: 14 }}>
        <button className="button" type="button" onClick={() => void onShare()}>
          Share result
        </button>

        <div className="button-row">
          <button className="button button--secondary" type="button" onClick={game.restart}>
            Play again
          </button>
          <button
            className="button button--secondary"
            type="button"
            onClick={() => void openLeaderboard()}
          >
            Leaderboard
          </button>
        </div>
      </div>

      {shareNote ? <p className="notice">{shareNote}</p> : null}

      {board ? (
        <section className="leaderboard">
          <div className="history__head">
            <h2 className="history__title">Today’s leaderboard</h2>
            <span className="history__summary">
              {board.players === 1 ? 'just you so far' : `${board.players} players`}
            </span>
          </div>

          <label className="field">
            <span className="field__label">
              Your name on the board — optional, and stored only in this browser
            </span>
            <input
              className="field__input"
              type="text"
              value={name}
              maxLength={20}
              placeholder="Anonymous"
              onChange={(event) => setName(event.target.value)}
              // Saved on the way out of the field, and the save renames the scores
              // already posted - so the row above stops saying Anonymous rather than
              // waiting for tomorrow's game.
              // Awaited, not fired and forgotten: reloading the board before the rename
              // has landed shows the old name back, which looks exactly like the bug
              // this was meant to fix.
              onBlur={() => void game.setName(name).then(() => openLeaderboard(true))}
            />
          </label>

          {board.entries.length === 0 ? (
            <p className="history__empty">Nobody has finished this one yet.</p>
          ) : (
            <ul className="leaderboard__list">
              {board.entries.map((entry) => (
                <li
                  className={
                    entry.you ? 'leaderboard__row leaderboard__row--you' : 'leaderboard__row'
                  }
                  key={`${entry.rank}-${entry.name}`}
                >
                  <span className="leaderboard__rank">{entry.rank}</span>
                  <span className="leaderboard__name">
                    {entry.name}
                    {entry.you ? <span className="leaderboard__you"> you</span> : null}
                  </span>
                  <span className="leaderboard__score">{entry.score}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Placed below the listed top: your own row, with the rank it really has. */}
          {board.you ? (
            <ul className="leaderboard__list leaderboard__list--yours">
              <li className="leaderboard__row leaderboard__row--you">
                <span className="leaderboard__rank">{board.you.rank}</span>
                <span className="leaderboard__name">
                  {board.you.name}
                  <span className="leaderboard__you"> you</span>
                </span>
                <span className="leaderboard__score">{board.you.score}</span>
              </li>
            </ul>
          ) : null}

          <p className="entry__note">
            Scores are checked by replaying every move on the server. That proves a score
            was possible on this board — it cannot prove who was at the keyboard.
          </p>
        </section>
      ) : null}

      {boardError ? <p className="notice notice--error">{boardError}</p> : null}

      <p className="entry__note" style={{ marginTop: 18 }}>
        A new puzzle every day at midnight UTC.
      </p>
    </div>
  );
}
